import React, { useState } from 'react';
import { useAppNavigation } from '../../hooks/useAppNavigation';
import {
  Box,
  Container,
  Typography,
  Card,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Chip,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useContinents, useUserMarketGroupings } from '../../hooks/useMarketCompassApi';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { brandColors } from '../../theme';
import { ReportContents } from '../../components/marketing/ReportContents';
import { HeroSection } from '../../components/marketing/HeroSection';
import { Testimonial } from '../../components/marketing/Testimonial';
import { SecondaryFeatures } from '../../components/marketing/SecondaryFeatures';
import { SEO } from '../../components/SEO';
import { generateBreadcrumbSchema, generateAggregateOfferSchema } from '../../components/SEO/schemas';
import { MarketOverviewSection } from '../../components/markets/MarketOverviewSection';
import { FAQSection, FAQTemplates } from '../../components/FAQ/FAQSection';
import { SampleReportsModal } from '../../components/marketing/SampleReportsModal';
import 'flag-icons/css/flag-icons.min.css';

const createSlug = (name: string): string => {
  return name.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const getContinentImagePath = (continentName: string): string => {
  const imageName = createSlug(continentName);
  return `/images/continents/${imageName}.png`;
};

interface PopularCountry {
  country: string;
  continent: string;
  code: string;
}

const popularCountries: PopularCountry[] = [
  { country: 'United States', continent: 'North America', code: 'us' },
  { country: 'Costa Rica', continent: 'North America', code: 'cr' },
  { country: 'Mexico', continent: 'North America', code: 'mx' },
  { country: 'Cayman Islands', continent: 'North America', code: 'ky' },
  { country: 'Turks & Caicos', continent: 'North America', code: 'tc' },
  { country: 'Bermuda', continent: 'North America', code: 'bm' },
];

const ContinentsPage: React.FC = () => {
  const { navigateTo } = useAppNavigation();
  const { hasStorePrivileged } = useUserPermissions();
  const [sampleReportsOpen, setSampleReportsOpen] = useState(false);

  // Breadcrumb schema for SEO
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Global Market Reports', url: '/global-market-reports' }
  ]);

  // Pricing schema for SEO (aggregating individual market, country, and continent pricing)
  const pricingSchema = generateAggregateOfferSchema({
    name: "HTL MarketCompass Subscription Plans",
    lowPrice: 200, // Individual market monthly subscription
    highPrice: 2000, // Continent-level monthly subscription
    priceCurrency: "USD",
    url: typeof window !== 'undefined' ? window.location.href : 'https://htlmarketcompass.com/global-market-reports',
    description: "Subscribe to hotel market analysis reports for individual markets, countries, or continents. Flexible pricing for every need."
  });
  const marketsCovered = `Alaska State Area • Albany, NY • Albuquerque, NM • Allentown-Reading, PA • Atlanta, GA • Augusta, GA • Austin, TX • Baltimore, MD • Birmingham, AL • Boston, MA • Buffalo, NY • California Central Coast • Central New Jersey • Charleston, SC • Charlotte, NC • Chattanooga, TN • Chicago, IL • Cincinnati, OH • Cleveland, OH • Colorado Springs, CO • Colorado State Area • Columbia, SC • Columbus, OH • Connecticut State Area • Dallas, TX • Daytona Beach, FL • Denver, CO • Des Moines, IA • Detroit, MI • Florida Keys • Fort Lauderdale, FL • Fort Myers, FL • Fort Worth-Arlington, TX • Gatlinburg-Pigeon Forge, TN • Grand Rapids-Michigan West • Greensboro-Winston Salem, NC • Greenville-Spartanburg, SC • Harrisburg, PA • Hartford, CT • Houston, TX • Indiana North State Area • Indiana South State Area • Indianapolis, IN • Inland Empire, CA • Jackson, MS • Jacksonville, FL • Kansas City, KS-MO • Kauai Island, HI • Knoxville, TN • Las Vegas, NV • Lexington, KY • Little Rock, AR • Long Island, NY • Los Angeles, CA • Louisville, KY • Lower Hudson Valley, NY • Macon-Warner Robins, GA • Madison, WI • Maine State Area • Maui Island, HI • McAllen-Brownsville, TX • Melbourne, FL • Memphis, TN • Miami, FL • Michigan North State Area • Michigan South State Area • Milwaukee, WI • Minneapolis, MN • Mobile, AL • Myrtle Beach, SC • Nashville, TN • New Jersey Shore • New Orleans, LA • New York, NY • Newark, NJ • Norfolk-Virginia Beach, VA • North Dakota State Area • Oahu Island, HI • Oakland, CA • Oklahoma City, OK • Omaha, NE • Orange County, CA • Orlando, FL • Palm Beach, FL • Philadelphia, PA • Phoenix, AZ • Pittsburgh, PA • Portland, ME • Portland, OR • Raleigh-Durham-Chapel Hill, NC • Rhode Island State Area • Richmond-Petersburg, VA • Rochester, NY • Sacramento, CA • Saint Louis, MO • Salt Lake City-Ogden, UT • San Antonio, TX • San Diego, CA • San Francisco, CA • San Jose-Santa Cruz, CA • Sarasota, FL • Savannah, GA • Seattle, WA • South Carolina State Area • Syracuse, NY • Tampa, FL • Tucson, AZ • Tulsa, OK • Washington, DC • Washington State Area`;

  const { data: continents = [], isLoading: loadingContinents, error: continentsError } = useContinents();
  const { data: marketGroupings = [] } = useUserMarketGroupings();

  const handleContinentClick = (continentName: string) => {
    const continentSlug = createSlug(continentName);
    navigateTo(`/global-market-reports/${continentSlug}`);
  };

  const handleCountryClick = (country: PopularCountry) => {
    const continentSlug = createSlug(country.continent);
    // Special handling for UK to maintain correct routing
    const countrySlug = country.country === 'UK' ? 'united-kingdom' : createSlug(country.country);
    navigateTo(`/global-market-reports/${continentSlug}/${countrySlug}`);
  };

  const handleGroupingClick = (groupingId: number) => {
    navigateTo(`/market-groupings/${groupingId}`);
  };

  if (loadingContinents) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading markets...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (continentsError) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ my: 4 }}>
          Failed to load market data. Please refresh the page.
        </Alert>
      </Container>
    );
  }

  // Create continent data matching the dashboard format
  // Filter out inactive continents for users without store.privileged permission
  const continentData = continents
    .filter(continent => hasStorePrivileged || continent.isActive)
    .map(continent => ({
      name: continent.name,
      displayName: continent.name === 'Asia' ? 'Asia & Middle East' : continent.name,
      count: `${hasStorePrivileged ? continent.marketCount : continent.activeMarketCount}+`,
      isActive: continent.isActive,
    }));

  // Check if there are any inactive continents being displayed
  const hasInactiveContinents = continentData.some(c => !c.isActive);

  return (
    <>
      <SEO
        title="Global Hotel Market Reports by Region"
        description="Explore comprehensive hospitality market reports for all continents. Access detailed hotel analytics, ADR, RevPAR, and occupancy data for North America, Europe, Asia, and more."
        canonical="/global-market-reports"
        schemas={[breadcrumbSchema, pricingSchema]}
      />
      <HeroSection showExploreButton={false} showSampleButton={false} align="top" textAlign="left" showDivider={false} showSubheading={false} />
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: brandColors.white,
        }}
      >
        <Container maxWidth="lg">
          {/* Show warning when inactive continents are displayed with special access */}
          {hasInactiveContinents && hasStorePrivileged && (
            <Alert severity="warning" sx={{ mb: 4 }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                Special Access View
              </Typography>
              <Typography variant="body2">
                Some continents shown are flagged as Internal Only which means they are only visible to Horwath HTL licensees and not regular users.
              </Typography>
            </Alert>
          )}
          
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h6"
              sx={{
                color: brandColors.mediumGray,
                fontWeight: 400,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              Most North American markets are live and additional markets from around the world will be rolling out over the coming months. For assistance accessing any of our 600+ markets or any submarket, please contact{' '}
              <a
                href="mailto:byounge@horwathhtl.com"
                style={{
                  color: brandColors.blue,
                  textDecoration: 'underline',
                }}
              >
                byounge@horwathhtl.com
              </a>.
            </Typography>
          </Box>

          {/* Continent Grid */}
          <Grid container spacing={3} sx={{ mb: 6, justifyContent: 'center' }}>
            {continentData.map((data, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  onClick={() => handleContinentClick(data.name)}
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: `1px solid ${brandColors.lightGray}`,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: `${brandColors.gold}10`,
                      borderColor: brandColors.gold,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  {!data.isActive && (
                    <Chip
                      label="Internal Only"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: '#FFCC00',
                        color: 'black',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={getContinentImagePath(data.name)}
                      alt={data.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        maxWidth: 120,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: brandColors.indigo,
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    {data.displayName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: brandColors.mediumGray,
                    }}
                  >
                    {data.count} markets
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Sample Countries Carousel */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: brandColors.indigo,
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
              }}
            >
              Popular Countries
            </Typography>
            
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Grid container spacing={2} sx={{ maxWidth: 1000 }}>
                  {popularCountries.map((country, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                      <Card
                        onClick={() => handleCountryClick(country)}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: `${brandColors.blue}10`,
                            transform: 'scale(1.05)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        <Box
                          className={`fi fi-${country.code}`}
                          sx={{
                            fontSize: 48,
                            mb: 1,
                            display: 'inline-block',
                            lineHeight: 1,
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: brandColors.indigo,
                            mb: 1,
                          }}
                        >
                          {country.country}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: brandColors.blue,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            opacity: 0.8,
                            fontSize: '0.75rem',
                          }}
                        >
                          View Markets <ArrowForwardIcon fontSize="small" />
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Box>

          {/* Standalone Sample Reports button below the list */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => setSampleReportsOpen(true)}
              sx={{
                borderColor: brandColors.blue,
                color: brandColors.blue,
                px: 4,
                py: 1.5,
                fontSize: '1.125rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                borderWidth: 2,
                '&:hover': {
                  borderColor: brandColors.blue,
                  backgroundColor: 'rgba(32, 76, 229, 0.05)',
                  borderWidth: 2,
                },
                transition: 'all 0.3s ease',
                minWidth: { xs: 200, sm: 'auto' },
              }}
            >
              View Sample Reports
            </Button>
          </Box>

          {/* Market Groupings Section */}
          {marketGroupings.length > 0 && (
            <Box sx={{ mt: 6, mb: 6 }}>
              <Typography
                variant="h5"
                sx={{
                  color: brandColors.indigo,
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Market Groupings
              </Typography>

              <Grid container spacing={2} sx={{ maxWidth: 1000, mx: 'auto' }}>
                {marketGroupings.map((grouping) => (
                  <Grid item xs={12} sm={6} md={4} lg={2} key={grouping.id}>
                    <Card
                      onClick={() => handleGroupingClick(grouping.id)}
                      sx={{
                        p: 0,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        overflow: 'hidden',
                        '&:hover': {
                          backgroundColor: `${brandColors.blue}10`,
                          transform: 'scale(1.05)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        },
                      }}
                    >
                      {/* Grouping Image */}
                      <Box
                        sx={{
                          width: '100%',
                          height: 120,
                          overflow: 'hidden',
                        }}
                      >
                        {grouping.product_image_url ? (
                          <Box
                            component="img"
                            src={grouping.product_image_url}
                            alt={grouping.name}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: brandColors.lightGray,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: brandColors.mediumGray,
                                textAlign: 'center',
                                px: 1,
                              }}
                            >
                              No Image
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: brandColors.indigo,
                            mb: 1,
                          }}
                        >
                          {grouping.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: brandColors.mediumGray,
                            mb: 1,
                            fontSize: '0.875rem',
                          }}
                        >
                          {grouping.market_count} markets
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: brandColors.blue,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            opacity: 0.8,
                            fontSize: '0.75rem',
                          }}
                        >
                          View Markets <ArrowForwardIcon fontSize="small" />
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Container>
      </Box>
      
      <ReportContents />

      {/* Markets Covered (copied from marketing dashboard) */}
      <Box sx={{ backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="lg" sx={{ pt: 2, pb: 6 }}>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Markets Covered
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.9 }}>
            {marketsCovered}
          </Typography>
        </Container>
      </Box>

      {/* Static SEO Content for Global Markets */}
      <MarketOverviewSection
        marketName="Global"
        description="Access comprehensive hospitality market intelligence with 150+ North American market reports currently available. Our expanded global coverage across Europe, Asia-Pacific, Middle East, Africa, and Latin America will launch in Fall 2025, providing detailed analysis of hotel performance metrics, development pipelines, and investment opportunities in established and emerging markets."
        keyMetrics={{
          adr: "152",
          revpar: "98",
          occupancy: "64.5",
          supply: "150+"
        }}
        showDetailedContent={false}
      />

      {/* Quote/Testimonial section */}
      <Testimonial />

      {/* Why Choose MarketCompass */}
      <SecondaryFeatures />

      {/* FAQ Section for SEO - PUBLIC PAGE */}
      <FAQSection
        faqs={[
          ...FAQTemplates.marketCoverage,
          ...FAQTemplates.dataMethodology,
        ]}
        title="Global Market Reports FAQ"
        subtitle="Learn more about our comprehensive hospitality market intelligence platform"
      />

      {/* Sample Reports Modal */}
      <SampleReportsModal
        open={sampleReportsOpen}
        onClose={() => setSampleReportsOpen(false)}
      />
    </>
  );
};

export default ContinentsPage;